import { useFormContext, type FieldValues, type Path } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Props<T extends FieldValues> {
  name: Path<T>;
  label: string;
  required?: boolean;
  type?: "text" | "email" | "number" | "tel";
  placeholder?: string;
  textarea?: boolean;
  rows?: number;
  help?: string;
  autoUpper?: boolean;
}

export function FieldInput<T extends FieldValues>({
  name,
  label,
  required,
  type = "text",
  placeholder,
  textarea,
  rows = 3,
  help,
  autoUpper,
}: Props<T>) {
  const {
    register,
    formState: { errors },
  } = useFormContext<T>();
  const error = (errors as any)?.[name]?.message as string | undefined;

  const isPhone = type === "tel" || name === "phone" || name === "whatsapp";

  const common = {
    id: name,
    placeholder: isPhone ? placeholder || "9876543210" : placeholder,
    "aria-invalid": !!error,
    ...register(name, {
      onChange: isPhone
        ? (e: React.ChangeEvent<HTMLInputElement>) => {
            let val = e.target.value.replace(/\D/g, "");
            if (val.startsWith("91") && val.length > 10) {
              val = val.slice(2);
            }
            e.target.value = val.slice(0, 10);
          }
        : undefined,
      setValueAs:
        type === "number"
          ? (v: any) => (v === "" || v == null ? undefined : Number(v))
          : isPhone
            ? (v: any) => {
                if (typeof v !== "string" || !v.trim())
                  return v === "" ? undefined : v;
                let clean = v.trim().replace(/\D/g, "");
                if (clean.startsWith("91") && clean.length > 10) {
                  clean = clean.slice(2);
                }
                const digits = clean.slice(0, 10);
                if (digits.length === 0) return undefined;
                return `+91${digits}`;
              }
            : autoUpper
              ? (v: any) => (typeof v === "string" ? v.toUpperCase() : v)
              : undefined,
    }),
  } as any;

  return (
    <div>
      <Label htmlFor={name}>
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      {isPhone ? (
        <div className="flex rounded-md border border-input focus-within:ring-1 focus-within:ring-ring">
          <span className="inline-flex items-center px-3 rounded-l-md bg-muted text-muted-foreground text-sm font-semibold select-none border-r border-input">
            +91
          </span>
          <Input
            type="tel"
            maxLength={10}
            className="rounded-l-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            {...common}
          />
        </div>
      ) : textarea ? (
        <Textarea rows={rows} {...common} />
      ) : (
        <Input type={type} {...common} />
      )}
      {error ? (
        <p className="text-xs text-destructive mt-1">{error}</p>
      ) : help ? (
        <p className="text-xs text-muted-foreground mt-1">{help}</p>
      ) : null}
    </div>
  );
}

export default FieldInput;
